import {
  getRecaptchaToken,
  isRecaptchaErrorCode,
  type RecaptchaErrorCode,
} from '@/js/utils/getRecaptchaToken';
import { getBitrixSessid } from '@/js/utils/getBitrixSessid';

export type SubmitWithCaptchaErrorCode =
  | RecaptchaErrorCode
  | 'csrf-missing'
  | 'submit-failed'
  | 'network-failed';

export interface SubmitFormWithCaptchaResult<T = { success?: boolean; msg?: string }> {
  ok: boolean;
  data?: T;
  errorCode?: SubmitWithCaptchaErrorCode;
  message?: string;
}

interface SubmitFormWithCaptchaOptions {
  endpoint: string;
  buildFormData: () => FormData;
}

const parseResponseJson = async <T>(response: Response): Promise<T | null> => {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
};

export const submitFormWithCaptcha = async <
  T extends {
    success?: boolean;
    msg?: string;
  },
>(
  options: SubmitFormWithCaptchaOptions,
): Promise<SubmitFormWithCaptchaResult<T>> => {
  try {
    const sessid = getBitrixSessid();
    if (!sessid) {
      return {
        ok: false,
        errorCode: 'csrf-missing',
        message: 'Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.',
      };
    }

    const token = await getRecaptchaToken();
    const body = options.buildFormData();
    body.set('sessid', sessid);
    body.set('token', token);

    const response = await fetch(options.endpoint, {
      method: 'POST',
      body,
      credentials: 'same-origin',
      headers: {
        'X-Bitrix-Csrf-Token': sessid,
      },
    });

    const result = await parseResponseJson<T>(response);
    if (!result) {
      return {
        ok: false,
        errorCode: 'submit-failed',
        message: 'Сервер вернул некорректный ответ',
      };
    }

    const businessFailed = result.success === false;
    const ok = response.ok && !businessFailed;

    if (!ok) {
      return {
        ok: false,
        data: result,
        errorCode: 'submit-failed',
        message: result.msg ?? 'Не удалось отправить форму',
      };
    }

    return { ok: true, data: result };
  } catch (error: unknown) {
    if (isRecaptchaErrorCode(error)) {
      return {
        ok: false,
        errorCode: error,
      };
    }

    return {
      ok: false,
      errorCode: 'network-failed',
      message: 'Ошибка соединения',
    };
  }
};
